import { useEffect, useState } from "react";
import {
  Modal,
  ModalDialog,
  Box,
  Typography,
  Button,
  Divider,
  Input,
} from "@mui/joy";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import { apiBaseUrl } from "../../utils/constants";

/* ── Confidence badge ─────────────────────────────────────────────────── */
const CONFIDENCE_STYLES: Record<string, { color: string; bg: string; label: string }> = {
  high:   { color: "#059669", bg: "#ecfdf5", label: "HIGH" },
  medium: { color: "#d97706", bg: "#fffbeb", label: "MEDIUM" },
  low:    { color: "#dc2626", bg: "#fef2f2", label: "LOW" },
};

const ConfidenceBadge = ({ level }: { level?: string }) => {
  const c = level ? CONFIDENCE_STYLES[level] : null;
  if (!c) return null;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "1px 6px",
        borderRadius: 4,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.05em",
        marginLeft: 8,
        color: c.color,
        background: c.bg,
        border: `1px solid ${c.color}44`,
      }}
    >
      {c.label}
    </span>
  );
};

/* ── Row wrapper ──────────────────────────────────────────────────────── */
const FieldRow = ({
  label,
  confidence,
  children,
}: {
  label: string;
  confidence?: string;
  children: React.ReactNode;
}) => (
  <Box sx={{ mb: 2 }}>
    <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
      <Typography level="body-sm" fontWeight={600} textColor="text.primary">
        {label}
      </Typography>
      <ConfidenceBadge level={confidence} />
    </Box>
    {children}
  </Box>
);

/* ── Shared field input style ─────────────────────────────────────────── */
const fieldSx = { fontSize: 14, "--Input-focusedHighlight": "var(--joy-palette-primary-500)" };

/* ── ExtractionValues type ────────────────────────────────────────────── */
export interface ExtractionValues {
  invoice_number: string;
  invoice_date: string;
  base_amount: string;
  total_tax: string;
  invoice_amount: string;
  net_payable: string;
}

/* ── Props ────────────────────────────────────────────────────────────── */
interface Props {
  open: boolean;
  invoiceId?: string | null; // Deprecated: no longer used with synchronous extraction
  extracted: {
    fields: Record<string, any>;
    confidence: Record<string, any>;
    original_filename?: string;
  } | null;
  pdfFile?: File | null; // New: pass the file object for preview
  onConfirm: (values: ExtractionValues) => void;
  onCancel: () => void;
}

/* ─────────────────────────────────────────────────────────────────────── */
const ExtractionReviewModal = ({ open, invoiceId, extracted, pdfFile, onConfirm, onCancel }: Props) => {
  const [values, setValues] = useState<ExtractionValues>({
    invoice_number: "",
    invoice_date: "",
    base_amount: "",
    total_tax: "",
    invoice_amount: "",
    net_payable: "",
  });

  useEffect(() => {
    if (!extracted) return;
    const f = extracted.fields || {};
    setValues({
      invoice_number: f.invoice_number || "",
      invoice_date: f.invoice_date || "",
      base_amount: f.base_amount != null ? String(f.base_amount) : "",
      total_tax: f.total_tax != null ? String(f.total_tax) : "",
      invoice_amount: f.invoice_amount != null ? String(f.invoice_amount) : "",
      net_payable: f.invoice_amount != null ? String(f.invoice_amount) : "",
    });
  }, [extracted]);

  if (!extracted) return null;

  const confidence = extracted.confidence || {};

  const setField = (k: keyof ExtractionValues) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setValues((prev) => {
      const next = { ...prev, [k]: v };
      // Keep net_payable in sync with invoice_amount unless user diverged them
      if (k === "invoice_amount" && prev.net_payable === prev.invoice_amount) {
        next.net_payable = v;
      }
      return next;
    });
  };

  const base = parseFloat(values.base_amount);
  const tax = parseFloat(values.total_tax);
  const inv = parseFloat(values.invoice_amount);
  const reconOk = ![base, tax, inv].some(Number.isNaN) && Math.abs(base + tax - inv) <= 1.0;
  const reconDiff = ![base, tax, inv].some(Number.isNaN) ? Math.abs(base + tax - inv) : null;

  const canConfirm =
    values.invoice_number.trim() !== "" &&
    values.invoice_date !== "" &&
    values.base_amount !== "" &&
    values.total_tax !== "" &&
    values.invoice_amount !== "" &&
    values.net_payable !== "";

  // Build the PDF preview: use pdfFile if available, fallback to invoiceId for backward compatibility
  const pdfPreviewUrl = pdfFile ? null : invoiceId ? `${apiBaseUrl}/invoice-extract-file/${invoiceId}` : null;

  const handleModalClose = (_event: {}, reason: string) => {
    if (reason === "backdropClick") return;
    onCancel();
  };

  return (
    <Modal open={open} onClose={handleModalClose} sx={{ zIndex: 1300 }}>
      <ModalDialog
        layout="fullscreen"
        sx={{
          p: 0,
          maxWidth: "1200px",
          width: "95vw",
          height: "88vh",
          mx: "auto",
          my: "auto",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          borderRadius: "sm",
        }}
      >
        {/* ── Header ── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Box>
            <Typography level="title-lg" sx={{ color: "var(--brand, #1a56db)" }}>
              Review Extracted Details
            </Typography>
            <Typography level="body-xs" textColor="text.secondary" sx={{ mt: 0.25 }}>
              Edit any field before filling the main form. Fields with lower confidence are marked.
            </Typography>
          </Box>
          <Button
            variant="plain"
            color="neutral"
            size="sm"
            onClick={onCancel}
            sx={{ minWidth: 0 }}
            aria-label="Close"
          >
            <CloseIcon fontSize="small" />
          </Button>
        </Box>

        {/* ── Body: two-column ── */}
        <Box
          sx={{
            flex: 1,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
            minHeight: 0,
          }}
        >
          {/* Left: PDF preview */}
          <Box
            sx={{
              borderRight: { xs: "none", lg: "1px solid" },
              borderBottom: { xs: "1px solid", lg: "none" },
              borderColor: "divider",
              bgcolor: "background.level1",
              minHeight: { xs: 220, lg: "unset" },
              position: "relative",
              overflow: "hidden",
            }}
          >
            {pdfFile ? (
              <PdfPreviewFromFile file={pdfFile} />
            ) : pdfPreviewUrl ? (
              <PdfPreview url={pdfPreviewUrl} filename={extracted.original_filename} />
            ) : (
              <Box sx={{ p: 3 }}>
                <Typography level="body-sm" textColor="text.secondary">
                  No preview available.
                </Typography>
              </Box>
            )}
          </Box>

          {/* Right: Editable fields */}
          <Box sx={{ overflowY: "auto", p: 3, bgcolor: "background.surface" }}>
            {confidence.used_vision && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 2,
                  p: 1.5,
                  borderRadius: "sm",
                  bgcolor: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  color: "#1d4ed8",
                }}
              >
                <Typography level="body-xs" sx={{ color: "inherit" }}>
                  Scanned PDF detected — extracted via vision model. Please verify amounts carefully.
                </Typography>
              </Box>
            )}

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                columnGap: 2,
              }}
            >
              <FieldRow label="Invoice Number" confidence={confidence.invoice_number}>
                <Input
                  size="sm"
                  sx={fieldSx}
                  value={values.invoice_number}
                  onChange={setField("invoice_number")}
                  placeholder="Invoice number"
                  slotProps={{ input: { "data-testid": "modal-field-invoice-number" } }}
                />
              </FieldRow>

              <FieldRow label="Invoice Date" confidence={confidence.invoice_date}>
                <Input
                  size="sm"
                  type="date"
                  sx={fieldSx}
                  value={values.invoice_date}
                  onChange={setField("invoice_date")}
                  slotProps={{ input: { "data-testid": "modal-field-invoice-date" } }}
                />
              </FieldRow>

              <FieldRow label="Base Amount (₹)" confidence={confidence.base_amount}>
                <Input
                  size="sm"
                  type="number"
                  sx={fieldSx}
                  value={values.base_amount}
                  onChange={setField("base_amount")}
                  slotProps={{
                    input: { step: "0.01", min: "0", "data-testid": "modal-field-base-amount" },
                  }}
                />
              </FieldRow>

              <FieldRow label="Total Tax (₹)" confidence={confidence.total_tax}>
                <Input
                  size="sm"
                  type="number"
                  sx={fieldSx}
                  value={values.total_tax}
                  onChange={setField("total_tax")}
                  slotProps={{
                    input: { step: "0.01", min: "0", "data-testid": "modal-field-total-tax" },
                  }}
                />
              </FieldRow>

              <FieldRow label="Invoice Amount (₹)" confidence={confidence.invoice_amount}>
                <Input
                  size="sm"
                  type="number"
                  sx={fieldSx}
                  value={values.invoice_amount}
                  onChange={setField("invoice_amount")}
                  slotProps={{
                    input: { step: "0.01", min: "0", "data-testid": "modal-field-invoice-amount" },
                  }}
                />
              </FieldRow>

              <FieldRow label="Net Payable (₹)">
                <Input
                  size="sm"
                  type="number"
                  sx={fieldSx}
                  value={values.net_payable}
                  onChange={setField("net_payable")}
                  slotProps={{
                    input: { step: "0.01", min: "0", "data-testid": "modal-field-net-payable" },
                  }}
                />
              </FieldRow>
            </Box>

            {/* Reconciliation banner */}
            {reconDiff !== null && (
              reconOk ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mt: 1,
                    p: 1.25,
                    borderRadius: "sm",
                    bgcolor: "#ecfdf5",
                    border: "1px solid #6ee7b7",
                  }}
                  data-testid="modal-recon-ok"
                >
                  <CheckCircleOutlineIcon sx={{ fontSize: 16, color: "#059669" }} />
                  <Typography level="body-xs" sx={{ color: "#065f46" }}>
                    Amounts reconciled: Base + Tax ≈ Invoice Amount (Δ ₹{reconDiff.toFixed(2)}).
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mt: 1,
                    p: 1.25,
                    borderRadius: "sm",
                    bgcolor: "#fffbeb",
                    border: "1px solid #fcd34d",
                  }}
                  data-testid="modal-recon-warn"
                >
                  <WarningAmberRoundedIcon sx={{ fontSize: 16, color: "#d97706" }} />
                  <Typography level="body-xs" sx={{ color: "#92400e" }}>
                    Base + Tax = ₹{(base + tax).toFixed(2)} does not match Invoice Amount = ₹
                    {inv.toFixed(2)} (Δ ₹{reconDiff.toFixed(2)}). Please verify.
                  </Typography>
                </Box>
              )
            )}
          </Box>
        </Box>

        {/* ── Footer ── */}
        <Divider />
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            px: 3,
            py: 2,
            bgcolor: "background.level1",
            flexShrink: 0,
          }}
        >
          <Button variant="outlined" color="neutral" onClick={onCancel} data-testid="modal-cancel-btn">
            Cancel
          </Button>
          <Button
            color="success"
            variant="soft"
            disabled={!canConfirm}
            onClick={() => onConfirm(values)}
            data-testid="modal-confirm-btn"
          >
            Fill Form &amp; Continue
          </Button>
        </Box>
      </ModalDialog>
    </Modal>
  );
};

/* ── PDF Preview: fetches with auth headers and creates an object URL ─── */
const PdfPreview = ({ url, filename }: { url: string; filename?: string }) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let revoked = false;
    const token = localStorage.getItem("authToken");
    const role = localStorage.getItem("userRole");
    fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        "X-User-Type": role || "",
        "ngrok-skip-browser-warning": "test",
      },
    })
      .then((r) => {
        if (!r.ok) throw new Error("Failed to load PDF");
        return r.blob();
      })
      .then((blob) => {
        if (!revoked) setObjectUrl(URL.createObjectURL(blob));
      })
      .catch(() => {
        if (!revoked) setError(true);
      });
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography level="body-sm" textColor="text.secondary">
          PDF preview unavailable.{" "}
          <a href={url} target="_blank" rel="noreferrer" style={{ color: "#2563eb" }}>
            Open in new tab
          </a>
        </Typography>
      </Box>
    );
  }

  if (!objectUrl) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
        <Typography level="body-sm" textColor="text.secondary">
          Loading preview…
        </Typography>
      </Box>
    );
  }

  return (
    <object
      data={objectUrl}
      type="application/pdf"
      style={{ width: "100%", height: "100%" }}
      aria-label="Invoice PDF preview"
      data-testid="modal-pdf-preview"
    >
      <Box sx={{ p: 3 }}>
        <Typography level="body-sm">
          PDF preview unavailable.{" "}
          <a href={objectUrl} target="_blank" rel="noreferrer" style={{ color: "#2563eb" }}>
            Open in new tab
          </a>
        </Typography>
      </Box>
    </object>
  );
};

/* ── PDF Preview from File object (for synchronous extraction) ─────────── */
const PdfPreviewFromFile = ({ file }: { file: File }) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setObjectUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [file]);

  if (!objectUrl) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
        <Typography level="body-sm" textColor="text.secondary">
          Loading preview…
        </Typography>
      </Box>
    );
  }

  return (
    <object
      data={objectUrl}
      type="application/pdf"
      style={{ width: "100%", height: "100%" }}
      aria-label="Invoice PDF preview"
      data-testid="modal-pdf-preview"
    >
      <Box sx={{ p: 3 }}>
        <Typography level="body-sm">
          PDF preview unavailable.
        </Typography>
      </Box>
    </object>
  );
};

export default ExtractionReviewModal;
